package at.theatervogelweide.admin.web.screens.news

import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.news.NewsDraft
import at.theatervogelweide.admin.contract.news.NewsListEntry
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.dateError
import at.theatervogelweide.admin.web.components.parseDateField
import at.theatervogelweide.domain.NewsPeriod
import at.theatervogelweide.domain.NewsVisibility
import at.theatervogelweide.domain.TheaterTime
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime

/** The editor's fields as typed; the days stay text until saving. */
data class NewsForm(
    val title: String = "",
    val text: String = "",
    val fromText: String = "",
    val toText: String = "",
    val publishUnderMyName: Boolean = false,
) {
    companion object {
        fun of(draft: NewsDraft) = NewsForm(
            title = draft.title,
            text = draft.text,
            fromText = draft.activeFrom?.let { TheaterTime.format(NewsPeriod.firstDay(it)) }.orEmpty(),
            toText = draft.activeTo?.let { TheaterTime.format(NewsPeriod.lastDay(it)) }.orEmpty(),
            publishUnderMyName = false,
        )
    }
}

/** A day field read into a timestamp; [Invalid] while it can't be read. */
sealed interface ParsedTimestamp {
    data class Valid(val value: LocalDateTime?) : ParsedTimestamp

    data object Invalid : ParsedTimestamp
}

private val MIDNIGHT = LocalTime(0, 0)

/** A day the person didn't touch keeps its exact stored time, so saving other changes never moves it. */
fun NewsForm.activeFrom(original: NewsDraft): ParsedTimestamp = parse(fromText, NewsForm.of(original).fromText, original.activeFrom) {
    NewsPeriod.activeFrom(it)
}

fun NewsForm.activeTo(original: NewsDraft): ParsedTimestamp = parse(toText, NewsForm.of(original).toText, original.activeTo) {
    NewsPeriod.activeTo(it)
}

private fun parse(
    text: String,
    originalText: String,
    original: LocalDateTime?,
    toTimestamp: (LocalDate) -> LocalDateTime,
): ParsedTimestamp = when {
    text == originalText -> ParsedTimestamp.Valid(original)
    text.isBlank() -> ParsedTimestamp.Valid(null)
    else -> parseDateField(text)?.let { ParsedTimestamp.Valid(toTimestamp(it)) } ?: ParsedTimestamp.Invalid
}

/** The draft to save, or the field errors that keep it from being saved; a [new] item needs "Veröffentlicht ab" (#151). */
fun NewsForm.toDraft(
    original: NewsDraft,
    imageUuids: List<String>,
    imageDetails: Map<String, ImageDetails> = emptyMap(),
    new: Boolean = false,
): Pair<NewsDraft?, List<FieldError>> {
    val dateErrors = listOfNotNull(
        dateError(fromText, optional = !new)?.let { FieldError(NewsDraft.ACTIVE_FROM, it) },
        dateError(toText, optional = true)?.let { FieldError(NewsDraft.ACTIVE_TO, it) },
    )
    val from = activeFrom(original) as? ParsedTimestamp.Valid
    val to = activeTo(original) as? ParsedTimestamp.Valid
    val draft = NewsDraft(
        title = title.trim(),
        text = text,
        activeFrom = from?.value,
        activeTo = to?.value,
        imageUuids = imageUuids,
        publishUnderMyName = publishUnderMyName,
        imageDetails = imageDetails,
    )
    val errors = dateErrors + draft.validate(new).filter { error -> dateErrors.none { it.field == error.field } }

    return if (errors.isEmpty() && from != null && to != null) draft to emptyList() else null to errors
}

/** What the visibility of the form is, falling back to the saved days while a field can't be read. */
fun NewsForm.visibility(original: NewsDraft, now: LocalDateTime): NewsVisibility {
    val from = (activeFrom(original) as? ParsedTimestamp.Valid)?.value ?: original.activeFrom
    val to = (activeTo(original) as? ParsedTimestamp.Valid)?.value ?: original.activeTo
    return NewsVisibility.of(from, to, now)
}

/**
 * The hint below "Veröffentlicht ab", whose day the website shows as the date (#151). Without a date only an older
 * item has one: a [new] item needs the date, and saving says so.
 */
fun NewsForm.fromHint(original: NewsDraft, new: Boolean = false): String? {
    val from = (activeFrom(original) as? ParsedTimestamp.Valid ?: return null).value
        ?: return if (new) null else "Ohne Datum ist die Neuigkeit sofort sichtbar, und die Website nennt bei ihr kein Datum."
    val local = TheaterTime.fromUtc(from)
    // only a time other than midnight, e.g. the prefilled now or one saved by the old editor, is named
    val time = if (local.time == MIDNIGHT) "" else ", ${TheaterTime.format(local.time)} Uhr"
    return "Ab ${TheaterTime.format(local.date)}$time auf der Website, mit diesem Tag als Datum."
}

/** The hint below "Veröffentlicht bis", which is inclusive: it names the first day the item is gone. */
fun NewsForm.toHint(original: NewsDraft): String? {
    val to = (activeTo(original) as? ParsedTimestamp.Valid ?: return null).value
        ?: return "Ohne Datum bleibt die Neuigkeit sichtbar."
    val local = TheaterTime.fromUtc(to)
    return if (local.time == MIDNIGHT) {
        "Ab ${TheaterTime.format(local.date)} nicht mehr sichtbar."
    } else {
        "Ab ${TheaterTime.format(local.date)}, ${TheaterTime.format(local.time)} Uhr nicht mehr sichtbar."
    }
}

/**
 * The list's meta line: "seit 01.10.2026", "ab 01.11.2026", "bis 31.08.2026"; an item without "Veröffentlicht ab"
 * starts with "ohne Datum", as the website shows no date for it (#151).
 */
fun NewsListEntry.periodText(now: LocalDateTime): String {
    val from = activeFrom?.let { TheaterTime.format(NewsPeriod.firstDay(it)) }
    val to = activeTo?.let { TheaterTime.format(NewsPeriod.lastDay(it)) }

    if (from == null) {
        return listOfNotNull("ohne Datum", to?.let { "bis $it" }).joinToString(" · ")
    }
    return when (visibility(now)) {
        NewsVisibility.PLANNED -> listOfNotNull("ab $from", to?.let { "bis $it" }).joinToString(" ")
        NewsVisibility.PUBLIC -> if (to != null) "bis $to" else "seit $from"
        NewsVisibility.EXPIRED -> "bis $to"
    }
}

val NewsVisibility.badgeVariant: BadgeVariant
    get() = when (this) {
        NewsVisibility.PUBLIC -> BadgeVariant.PUBLIC
        NewsVisibility.PLANNED -> BadgeVariant.PLANNED
        NewsVisibility.EXPIRED -> BadgeVariant.EXPIRED
    }
